import { Vec3 } from '@/core/math/Vec3';
import { TAU } from '@/core/math/scalar';
import { CelestialBody } from '@/universe/CelestialBody';
import { OrbitalElements, elementsFromState, isHyperbolic, orbitalPeriod, stateVectorAt } from '@/universe/OrbitalElements';
import { orbitalBasis, orbitalBasisDeltaV } from './ManeuverPlanner';
import { ChainedPrediction, TrajectoryPredictor } from './TrajectoryPredictor';

/** Components of a departure burn, in the orbital basis at the burn point. */
export interface BurnComponents {
  prograde: number;
  normal: number;
  radial: number;
}

export interface TransferSolution {
  /** When to be at the middle of the burn, seconds. */
  burnTime: number;
  components: BurnComponents;
  /** Magnitude of the velocity change, m/s. */
  deltaV: number;
  /** The chained route this burn produces. */
  route: ChainedPrediction;
  /** How close the route comes to the target, metres. */
  miss: number;
  /** True when the route enters the target's sphere of influence. */
  arrived: boolean;
  /** True when no arc hits a body. */
  safe: boolean;
  cost: number;
}

export interface TransferSolverOptions {
  predictor: TrajectoryPredictor;
  referenceBody: CelestialBody;
  /** Vessel state in `referenceBody`'s inertial frame. */
  position: Vec3;
  velocity: Vec3;
  /** Simulation time the state belongs to. */
  t0: number;
  target: CelestialBody;
  /** Delta-v the vessel actually has, m/s. Candidates beyond it are penalised. */
  availableDeltaV: number;
  /** Earliest the burn may start, seconds after `t0`. */
  minLead?: number;
  /** Hard cap on trajectory evaluations across the whole solve. */
  maxEvaluations?: number;
  /** Samples per conic. Lower is faster and coarser. */
  samples?: number;
  maxArcs?: number;
  totalHorizon?: number;
  terrainCeilingFor?: (body: CelestialBody) => number;
  /**
   * Fixes the burn time instead of searching for a departure window. Used by
   * the mid-course correction, which must burn now rather than wait.
   */
  fixedBurnTime?: number;
  /** Cap on each component of the search, m/s. Bounds a correction burn. */
  componentLimit?: number;
}

/** Any route that hits something outranks every route that does not. */
const UNSAFE_PENALTY = 1e14;
/** Weight on propellant, in cost-metres per m/s. Only a tie-breaker. */
const DELTA_V_WEIGHT = 1e3;
/** Fraction of the target's SOI that counts as an arrival. */
const CAPTURE_FRACTION = 0.85;

/**
 * Finds a departure burn whose gravity-propagated route reaches a target body
 * without flying through anything.
 *
 * This is deliberately *not* a trajectory optimiser. It is a bounded search
 * over a small parameter set — burn time plus the three orbital-basis
 * components — scored by propagating the real patched-conic chain and asking
 * two questions of the result: how close does it come to the target, and does
 * it hit anything on the way. Every candidate is evaluated by
 * `TrajectoryPredictor.predictChained`, so a route the solver approves is a
 * route real gravity was actually run against; any bending or assist the route
 * picks up along the way falls out of that propagation rather than being
 * planned for.
 *
 * The search is a pattern search (gradient-free coordinate descent with a
 * halving step), which suits a cost surface that is piecewise smooth with
 * cliffs at every sphere-of-influence boundary — a gradient method would fall
 * off those cliffs. It is not guaranteed to converge, and it says so: when the
 * evaluation budget runs out without a safe arriving route, the status is
 * `failed` and the caller is expected to tell the player, not to fly the best
 * unsafe candidate.
 *
 * Work is done in `step()`-sized slices so the caller can spread the solve
 * across several ticks instead of stalling a frame.
 */
export class TransferSolver {
  status: 'searching' | 'solved' | 'failed' = 'searching';
  /** Best candidate seen so far; may be unsafe or short while `searching`. */
  best: TransferSolution | null = null;
  evaluations = 0;
  failureReason: string | null = null;

  private readonly opts: Required<
    Pick<TransferSolverOptions, 'minLead' | 'maxEvaluations' | 'samples' | 'maxArcs' | 'totalHorizon'>
  > &
    TransferSolverOptions;
  private readonly elements: OrbitalElements;
  private readonly captureRadius: number;
  private readonly safePeriapsis: number;
  /** Arrival periapsis the search is nudged toward, metres from the centre. */
  readonly desiredPeriapsis: number;
  /** Vis-viva sizing of the transfer, before any search. Drives the estimate. */
  sizingDeltaV = 0;

  /** Departure windows still to be probed, cheapest guess first. */
  private candidates: { burnTime: number; guess: BurnComponents }[] = [];
  /** Pattern-search state, once a candidate has been promoted to refinement. */
  private refine: { burnTime: number; components: BurnComponents; step: number; timeStep: number } | null = null;

  constructor(options: TransferSolverOptions) {
    this.opts = {
      minLead: 20,
      maxEvaluations: 260,
      samples: 64,
      maxArcs: 5,
      totalHorizon: 60 * 86400,
      ...options,
    };
    this.elements = elementsFromState(options.position, options.velocity, options.referenceBody.mu, options.t0);
    this.captureRadius = options.target.sphereOfInfluence * CAPTURE_FRACTION;
    // Arriving on a trajectory whose periapsis is inside the target's air (or
    // its ground) is not an arrival, it is an entry. The macro's job stops at
    // capture, so the route has to leave room for one.
    this.safePeriapsis = options.target.spaceAltitudeRadius + options.target.radius * 0.15;
    this.desiredPeriapsis = Math.min(options.target.radius * 5, options.target.sphereOfInfluence * 0.25);
    this.buildCandidates();
  }

  /** Runs up to `budget` trajectory evaluations. Returns true when finished. */
  step(budget = 4): boolean {
    for (let i = 0; i < budget && this.status === 'searching'; i++) {
      if (this.evaluations >= this.opts.maxEvaluations) {
        this.finish();
        break;
      }
      if (this.refine) this.refineStep();
      else if (this.candidates.length > 0) this.probeStep();
      else this.promoteBest();
    }
    return this.status !== 'searching';
  }

  /** Runs the whole solve at once. For tests and headless callers. */
  solve(): TransferSolution | null {
    while (!this.step(16));
    return this.status === 'solved' ? this.best : null;
  }

  // ---- Search ---------------------------------------------------------------

  private probeStep(): void {
    const candidate = this.candidates.shift()!;
    this.consider(this.evaluate(candidate.burnTime, candidate.guess));
    if (this.candidates.length === 0) this.promoteBest();
  }

  private promoteBest(): void {
    if (!this.best) {
      this.failureReason = 'No departure window could be evaluated';
      this.status = 'failed';
      return;
    }
    const scale = Math.max(40, Math.abs(this.best.components.prograde) * 0.1);
    this.refine = {
      burnTime: this.best.burnTime,
      components: { ...this.best.components },
      step: scale,
      timeStep: this.opts.fixedBurnTime !== undefined ? 0 : Math.max(15, this.orbitPeriod() / 48),
    };
  }

  /**
   * One pass of the pattern search: try a step along each of the four axes in
   * both directions, take the best improvement, and halve the step when none
   * of them improves.
   */
  private refineStep(): void {
    const r = this.refine!;
    const limit = this.opts.componentLimit ?? Infinity;
    let improved: TransferSolution | null = null;

    const axes: (keyof BurnComponents)[] = ['prograde', 'normal', 'radial'];
    for (const axis of axes) {
      for (const sign of [1, -1]) {
        const components = { ...r.components };
        const next = components[axis] + sign * r.step;
        if (Math.abs(next) > limit) continue;
        components[axis] = next;
        const result = this.evaluate(r.burnTime, components);
        this.consider(result);
        if (!improved || result.cost < improved.cost) improved = result;
      }
    }

    if (r.timeStep > 0) {
      for (const sign of [1, -1]) {
        const burnTime = r.burnTime + sign * r.timeStep;
        if (burnTime < this.opts.t0 + this.opts.minLead) continue;
        const result = this.evaluate(burnTime, r.components);
        this.consider(result);
        if (!improved || result.cost < improved.cost) improved = result;
      }
    }

    const current = this.best!;
    if (improved && improved.cost < current.cost - 1e-6) {
      r.components = { ...improved.components };
      r.burnTime = improved.burnTime;
    } else if (improved && improved.cost < this.costOf(r)) {
      r.components = { ...improved.components };
      r.burnTime = improved.burnTime;
    } else {
      r.step *= 0.5;
      r.timeStep *= 0.5;
      // Re-centre on the best point found anywhere, not just on this walk, so a
      // shrinking step does not keep refining a worse local basin.
      r.components = { ...current.components };
      r.burnTime = current.burnTime;
      if (r.step < 2) this.finish();
    }
  }

  private costOf(r: { burnTime: number; components: BurnComponents }): number {
    return this.evaluate(r.burnTime, r.components).cost;
  }

  private finish(): void {
    const best = this.best;
    if (best && best.safe && best.arrived) {
      this.status = 'solved';
      return;
    }
    this.status = 'failed';
    if (!best) this.failureReason = 'No route could be evaluated';
    else if (this.sizingDeltaV > this.opts.availableDeltaV) {
      // The transfer's own vis-viva sizing is already out of budget, so every
      // candidate the search could reach was a cheap one that falls short.
      // Reporting the miss here would blame the wrong thing.
      this.failureReason =
        `Transfer needs about ${this.sizingDeltaV.toFixed(0)} m/s; ` +
        `${this.opts.availableDeltaV.toFixed(0)} m/s available`;
    } else if (!best.safe) {
      this.failureReason = `Every route found flies into ${best.route.impactBody?.name ?? 'a body'}`;
    } else if (best.deltaV > this.opts.availableDeltaV) {
      this.failureReason = `Requires ${best.deltaV.toFixed(0)} m/s; ${this.opts.availableDeltaV.toFixed(0)} m/s available`;
    } else {
      const miss = best.miss / 1000;
      this.failureReason = `Closest route misses ${this.opts.target.name} by ${miss.toFixed(0)} km`;
    }
  }

  private consider(result: TransferSolution): void {
    if (!this.best || result.cost < this.best.cost) this.best = result;
  }

  // ---- Scoring --------------------------------------------------------------

  private evaluate(burnTime: number, components: BurnComponents): TransferSolution {
    this.evaluations++;
    const at = stateVectorAt(this.elements, burnTime);
    const dv = orbitalBasisDeltaV(
      at.position,
      at.velocity,
      components.prograde,
      components.normal,
      components.radial,
    );
    const deltaV = dv.length();

    const route = this.opts.predictor.predictChained(
      this.opts.referenceBody,
      at.position,
      Vec3.addVectors(at.velocity, dv),
      burnTime,
      {
        samples: this.opts.samples,
        maxArcs: this.opts.maxArcs,
        totalHorizon: this.opts.totalHorizon,
        arriveAtBodyId: this.opts.target.id,
        terrainCeilingFor: this.opts.terrainCeilingFor,
      },
    );

    const hitSomething = route.impacts;
    const hitTarget = hitSomething && route.impactBody?.id === this.opts.target.id;
    const safe = !hitSomething;

    let cost = 0;
    if (hitSomething) cost += hitTarget ? UNSAFE_PENALTY * 0.5 : UNSAFE_PENALTY;
    cost += Math.max(0, route.targetMiss - this.captureRadius);
    if (route.arrived && Number.isFinite(route.targetPeriapsis)) {
      // Too low is a failure; too high is merely a bad arrival. A route that
      // grazes the far edge of the sphere of influence is technically an
      // arrival and a useless one — the capture burn there leaves a barely
      // bound orbit — so pull the approach in toward a sensible standoff, but
      // gently enough that it never outweighs not hitting the ground.
      cost += Math.max(0, this.safePeriapsis - route.targetPeriapsis) * 1e3;
      cost += Math.max(0, route.targetPeriapsis - this.desiredPeriapsis) * 0.08;
    }
    if (deltaV > this.opts.availableDeltaV) cost += (deltaV - this.opts.availableDeltaV) * 1e9;
    cost += deltaV * DELTA_V_WEIGHT;

    return {
      burnTime,
      components: { ...components },
      deltaV,
      route,
      miss: route.targetMiss,
      arrived: route.arrived && !hitTarget,
      safe,
      cost,
    };
  }

  // ---- Initial guesses ------------------------------------------------------

  private orbitPeriod(): number {
    const period = orbitalPeriod(this.elements);
    return Number.isFinite(period) && period > 0 ? period : 3600;
  }

  /**
   * Seeds the search with a handful of departure windows.
   *
   * The sizing is the standard patched-conic one: work out the velocity change
   * the transfer needs in the frame the target actually orbits (the lowest
   * common ancestor of the two bodies), then, if the vessel is down a gravity
   * well of its own, convert that hyperbolic excess into the ejection burn that
   * produces it — `v_burn = sqrt(v_escape^2 + v_infinity^2)`. When the target
   * already orbits the vessel's own reference body the escape term drops out
   * and this reduces to plain vis-viva.
   *
   * Timing comes from the phase condition: the transfer arrives roughly half an
   * orbit after it leaves, so the burn wants to happen when the target will be
   * where the transfer's far apsis ends up. Scanning that condition over a
   * couple of the vessel's own orbits finds the windows; when the vessel orbits
   * the same body as the target those recur every orbit, which is why this is
   * cheap. When they do not recur inside the scan the candidate list falls back
   * to evenly spaced burn points, and the solve usually fails honestly rather
   * than pretending.
   */
  private buildCandidates(): void {
    const { referenceBody, target, t0 } = this.opts;
    const earliest = t0 + this.opts.minLead;

    if (this.opts.fixedBurnTime !== undefined) {
      this.candidates.push({
        burnTime: Math.max(earliest, this.opts.fixedBurnTime),
        guess: { prograde: 0, normal: 0, radial: 0 },
      });
      return;
    }

    const sizing = sizeTransfer(
      this.opts.predictor,
      referenceBody,
      this.opts.position,
      this.opts.velocity,
      t0,
      target,
    );
    const { common, branch, transferTime } = sizing;

    const progradeAt = (burnTime: number): number => {
      const at = stateVectorAt(this.elements, burnTime);
      return departureDeltaV(sizing, referenceBody, at.position.length(), at.velocity.length());
    };

    this.sizingDeltaV = Math.abs(progradeAt(earliest));

    const windows = this.departureWindows(common, branch, transferTime, earliest);
    for (const burnTime of windows) {
      this.candidates.push({ burnTime, guess: { prograde: progradeAt(burnTime), normal: 0, radial: 0 } });
    }

    // Always keep a few evenly spaced near-term departures in the list. They
    // cover the case where the phase scan finds nothing (an out-of-phase
    // interplanetary target) with faster, more expensive transfers, and they
    // give the pattern search a second basin to fall into.
    const period = this.orbitPeriod();
    for (let i = 0; i < 6; i++) {
      const burnTime = earliest + (period * i) / 6;
      this.candidates.push({ burnTime, guess: { prograde: progradeAt(burnTime), normal: 0, radial: 0 } });
      this.candidates.push({ burnTime, guess: { prograde: progradeAt(burnTime) * 1.4, normal: 0, radial: 0 } });
    }
  }

  /**
   * Burn times at which a half-orbit transfer would arrive where the target
   * will be. Found by scanning the wrapped angle between the vessel's position
   * at the burn and the target's position one transfer time later, in the
   * vessel's own orbital plane, and keeping the sign changes.
   */
  private departureWindows(
    common: CelestialBody,
    branch: CelestialBody,
    transferTime: number,
    earliest: number,
  ): number[] {
    if (!Number.isFinite(transferTime) || transferTime <= 0) return [];
    const frames = this.opts.predictor.system.frames;
    const referenceBody = this.opts.referenceBody;

    const period = this.orbitPeriod();
    const span = Math.min(period * 2.5, 30 * 86400);
    const stepCount = 512;
    const stepSize = span / stepCount;

    const here = stateVectorAt(this.elements, earliest);
    const basis = orbitalBasis(here.position, here.velocity);
    const axisX = here.position.lengthSq() > 0 ? here.position.normalized() : basis.prograde.clone();
    const axisY = Vec3.crossVectors(basis.normal, axisX).normalize();

    const phase = (burnTime: number): number => {
      const at = stateVectorAt(this.elements, burnTime);
      const departure = Math.atan2(at.position.dot(axisY), at.position.dot(axisX));
      // The target's position, expressed where the vessel's conic lives.
      const targetIn = frames.transformPosition(
        new Vec3(),
        branch.inertialFrameId,
        common.id === referenceBody.id ? referenceBody.inertialFrameId : common.inertialFrameId,
        burnTime + transferTime,
      );
      const arrival = Math.atan2(targetIn.dot(axisY), targetIn.dot(axisX));
      return wrapPi(arrival - departure - Math.PI);
    };

    const windows: number[] = [];
    let prevT = earliest;
    let prev = phase(prevT);
    for (let i = 1; i <= stepCount && windows.length < 6; i++) {
      const t = earliest + i * stepSize;
      const value = phase(t);
      // Ignore the branch cut: a jump of more than half a turn is a wrap, not
      // a crossing.
      if (prev !== value && Math.sign(prev) !== Math.sign(value) && Math.abs(prev - value) < Math.PI) {
        let a = prevT;
        let b = t;
        for (let k = 0; k < 30; k++) {
          const m = (a + b) / 2;
          if (Math.sign(phase(m)) === Math.sign(prev)) a = m;
          else b = m;
        }
        windows.push((a + b) / 2);
      }
      prevT = t;
      prev = value;
    }
    return windows;
  }
}

/** Closed-form sizing of a transfer, before any search. */
export interface TransferSizing {
  /** Frame the transfer has to be planned in: the two bodies' common ancestor. */
  common: CelestialBody;
  /** The child of `common` on the path down to the target. */
  branch: CelestialBody;
  /** Speed change the transfer needs in `common`'s frame, m/s. Signed. */
  vInfinity: number;
  /** Half-period of the transfer ellipse — how long the coast takes, seconds. */
  transferTime: number;
  /** Rough capture burn into a standoff orbit at the target, m/s. */
  captureDeltaV: number;
}

/**
 * Vis-viva sizing of a transfer between two bodies.
 *
 * Split out of the solver so the macro can quote a delta-v and a duration to
 * the player before committing to a solve — the estimate and the search agree
 * because they are the same arithmetic, not two versions of it.
 */
export function sizeTransfer(
  predictor: TrajectoryPredictor,
  referenceBody: CelestialBody,
  position: Vec3,
  velocity: Vec3,
  t: number,
  target: CelestialBody,
): TransferSizing {
  const common = lowestCommonBody(referenceBody, target);
  const branch = childOnPathTo(common, target) ?? target;

  const stateInCommon = predictor.system.frames.transformState(
    position,
    velocity,
    referenceBody.inertialFrameId,
    common.inertialFrameId,
    t,
  );
  const r1 = Math.max(1, stateInCommon.position.length());
  const branchPosition = predictor.system.frames.transformPosition(
    new Vec3(),
    branch.inertialFrameId,
    common.inertialFrameId,
    t,
  );
  const r2 = Math.max(1, branchPosition.length());

  const aTransfer = (r1 + r2) / 2;
  const vTransfer = Math.sqrt(Math.max(0, common.mu * (2 / r1 - 1 / aTransfer)));
  const vInfinity = vTransfer - stateInCommon.velocity.length();
  const transferTime = Math.PI * Math.sqrt((aTransfer * aTransfer * aTransfer) / common.mu);

  // Capture: shed the difference between arrival speed at the standoff radius
  // and circular speed there. Approximating the arrival as parabolic makes this
  // the familiar (sqrt(2) - 1) * v_circular; it is an estimate for the player,
  // not the number the capture burn actually uses.
  const standoff = Math.max(1, Math.min(target.radius * 5, target.sphereOfInfluence * 0.25));
  const captureDeltaV = (Math.SQRT2 - 1) * Math.sqrt(target.mu / standoff);

  return { common, branch, vInfinity, transferTime, captureDeltaV };
}

/**
 * Departure burn magnitude that delivers a transfer's hyperbolic excess.
 *
 * When the target orbits the vessel's own reference body this is plain
 * vis-viva. Otherwise the burn has to buy escape as well as the excess, which
 * is the standard ejection relation `v_burn = sqrt(v_escape^2 + v_infinity^2)`
 * — and the reason a departure from low orbit costs so much less than the same
 * excess bought in open space.
 */
export function departureDeltaV(
  sizing: TransferSizing,
  referenceBody: CelestialBody,
  radius: number,
  speed: number,
): number {
  if (sizing.common.id === referenceBody.id) return sizing.vInfinity;
  const vEscape = Math.sqrt((2 * referenceBody.mu) / Math.max(1, radius));
  return Math.sqrt(vEscape * vEscape + sizing.vInfinity * sizing.vInfinity) - speed;
}

/** Deepest body that is an ancestor-or-self of both `a` and `b`. */
export function lowestCommonBody(a: CelestialBody, b: CelestialBody): CelestialBody {
  const chain = new Set<string>();
  for (let n: CelestialBody | null = a; n; n = n.parent) chain.add(n.id);
  for (let n: CelestialBody | null = b; n; n = n.parent) {
    if (chain.has(n.id)) return n;
  }
  // Every body in a system shares the star; this is unreachable in practice.
  let root: CelestialBody = a;
  while (root.parent) root = root.parent;
  return root;
}

/** The child of `ancestor` on the path down to `body`, or null. */
export function childOnPathTo(ancestor: CelestialBody, body: CelestialBody): CelestialBody | null {
  let node: CelestialBody | null = body;
  while (node && node.parent) {
    if (node.parent.id === ancestor.id) return node;
    node = node.parent;
  }
  return null;
}

function wrapPi(angle: number): number {
  let a = angle % TAU;
  if (a > Math.PI) a -= TAU;
  if (a < -Math.PI) a += TAU;
  return a;
}

/** True when the vessel's conic about `body` is unbound. */
export function escaping(position: Vec3, velocity: Vec3, body: CelestialBody, t: number): boolean {
  return isHyperbolic(elementsFromState(position, velocity, body.mu, t));
}
